import { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { PageHeader } from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import { Printer, Download } from "lucide-react";

/**
 * Printable QR poster for the "self check-in" URL. Print once, tape at the
 * academy entrance. Students scan → open the app → tap the class they're
 * attending → done.
 *
 * Owner-only. Uses `window.location.origin` so preview and production each
 * render a QR pointing at themselves — no hardcoded domain.
 */
export default function CheckinQr() {
  const { user } = useAuth();
  const academyName = user?.academy_name || "Your Academy";
  const url = useMemo(() => `${window.location.origin}/student/checkin`, []);

  const downloadSvg = () => {
    const svg = document.querySelector("#checkin-qr-svg");
    if (!svg) return;
    const src = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([src], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${academyName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-checkin-qr.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="page-checkin-qr">
      <PageHeader section="Operate" title="Self check-in QR"
        right={
          <div className="flex gap-2">
            <button onClick={downloadSvg} className="btn btn-outline" data-testid="qr-download-btn">
              <Download size={13}/> SVG
            </button>
            <button onClick={() => window.print()} className="btn btn-primary" data-testid="qr-print-btn">
              <Printer size={13}/> Print
            </button>
          </div>
        }
      />

      <div className="p-4 md:p-8">
        <div className="card p-6 md:p-10 max-w-2xl mx-auto text-center print:border-none" data-testid="qr-poster">
          <div className="label-tiny mb-1">{academyName.toUpperCase()} · CHECK-IN</div>
          <div className="text-[26px] md:text-[34px] font-bold uppercase tracking-tight">Scan to check in</div>
          <div className="label-tiny mt-2" style={{ color: "var(--text-dim)" }}>
            Escanea para registrar tu asistencia
          </div>

          <div className="mt-8 mb-6 flex justify-center">
            <div className="p-5 bg-white rounded-2xl inline-block" data-testid="qr-frame">
              <QRCodeSVG id="checkin-qr-svg" value={url} size={280} level="M" includeMargin={false} />
            </div>
          </div>

          <div className="mono text-[12px] break-all" style={{ color: "var(--text-dim)" }} data-testid="qr-url">
            {url}
          </div>

          <div className="divider my-6" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
            <div>
              <div className="font-semibold text-[13px] uppercase tracking-tight">1. Scan</div>
              <div className="label-tiny mt-1">Open your camera and point it at the QR code.</div>
            </div>
            <div>
              <div className="font-semibold text-[13px] uppercase tracking-tight">2. Pick your class</div>
              <div className="label-tiny mt-1">Only classes starting soon (or just started) will show.</div>
            </div>
            <div>
              <div className="font-semibold text-[13px] uppercase tracking-tight">3. Tap check-in</div>
              <div className="label-tiny mt-1">Coach sees you on the roster instantly. Oss!</div>
            </div>
          </div>

          <div className="mt-8 label-tiny" style={{ color: "var(--text-dim)" }}>
            Window: opens 15 min before class · closes 30 min after start · late arrival auto-marked.
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          nav, aside, header, [data-testid="mobile-menu-btn"] { display: none !important; }
          [data-testid="qr-poster"] {
            background: white !important;
            color: black !important;
            border: none !important;
            box-shadow: none !important;
          }
          [data-testid="qr-poster"] * { color: black !important; }
        }
      `}</style>
    </div>
  );
}
